import { useEffect, useRef, useState } from 'react';
import { Modal, Stack, Group, Text, Badge, Button, Loader, TextInput, Box, ScrollArea } from '@mantine/core';
import { priceLookupApi, type PriceLookupView, type VendorJobView } from '../api/client';

interface Props {
  opened: boolean;
  onClose: () => void;
  conversationId: number;
  quoteItemIdx: number;
  partName: string;
  partHint?: string;
  vendors?: string[];
  onApplied?: (vendorJobId: number, result: VendorJobView['result']) => void;
}

const POLL_MS = 1500;

const STATUS_COLOR: Record<VendorJobView['status'], string> = {
  queued: 'gray',
  in_progress: 'blue',
  needs_input: 'orange',
  done: 'green',
  failed: 'red',
  expired: 'red',
};

const STATUS_LABEL: Record<VendorJobView['status'], string> = {
  queued: 'Queued',
  in_progress: 'Fetching…',
  needs_input: 'Needs input',
  done: 'Done',
  failed: 'Failed',
  expired: 'Timed out',
};

export function PriceLookupModal({
  opened, onClose, conversationId, quoteItemIdx, partName, partHint, vendors = ['autozone'], onApplied,
}: Props) {
  const [lookup, setLookup] = useState<PriceLookupView | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Create lookup on open
  useEffect(() => {
    if (!opened) return;
    setError(null);
    setLookup(null);
    setCreating(true);
    priceLookupApi
      .create({ conversation_id: conversationId, quote_item_idx: quoteItemIdx, part_name: partName, part_hint: partHint, vendors })
      .then((v) => setLookup(v))
      .catch((e) => setError(e?.response?.data?.detail ?? String(e)))
      .finally(() => setCreating(false));
  }, [opened, conversationId, quoteItemIdx, partName, partHint, vendors.join(',')]);

  // Poll while open + any job non-terminal
  useEffect(() => {
    if (!lookup || lookup.status !== 'open') return;
    const allTerminal = lookup.vendor_jobs.every((j) => ['done', 'failed', 'expired'].includes(j.status));
    if (allTerminal) return;
    pollRef.current = setInterval(async () => {
      try {
        const v = await priceLookupApi.get(lookup.id);
        setLookup(v);
      } catch { /* swallow transient */ }
    }, POLL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [lookup?.id, lookup?.status, JSON.stringify(lookup?.vendor_jobs?.map((j) => j.status))]);

  const handleApply = async (jobId: number) => {
    if (!lookup) return;
    const job = lookup.vendor_jobs.find((j) => j.id === jobId);
    try {
      const v = await priceLookupApi.apply(lookup.id, jobId);
      setLookup(v);
      onApplied?.(jobId, job?.result);
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? String(e));
    }
  };

  const handleAnswer = async (jobId: number, answer: string) => {
    if (!lookup || !answer.trim()) return;
    try {
      const v = await priceLookupApi.answer(lookup.id, jobId, answer.trim());
      setLookup(v);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? String(e));
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={`Pricing — ${partName}`} size="lg">
      {creating && <Group justify="center"><Loader size="sm" /> <Text size="sm" c="dimmed">Queueing…</Text></Group>}
      {error && <Text size="sm" c="red" mb="sm">{error}</Text>}

      {lookup && (
        <Stack gap="sm">
          {lookup.vehicle && Object.keys(lookup.vehicle).length > 0 && (
            <Text size="xs" c="dimmed">
              {[lookup.vehicle.year, lookup.vehicle.make, lookup.vehicle.model].filter(Boolean).join(' ')}
              {lookup.vehicle.vin ? ` · VIN ${String(lookup.vehicle.vin).slice(-6)}` : ''}
            </Text>
          )}

          <ScrollArea.Autosize mah={420}>
            <Stack gap="xs">
              {lookup.vendor_jobs.map((j) => <VendorCard key={j.id} job={j} onApply={handleApply} onAnswer={handleAnswer} />)}
            </Stack>
          </ScrollArea.Autosize>

          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose}>Close</Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

function VendorCard({
  job, onApply, onAnswer,
}: {
  job: VendorJobView;
  onApply: (id: number) => void;
  onAnswer: (id: number, answer: string) => void;
}) {
  const [answer, setAnswer] = useState('');
  return (
    <Box style={{ border: '1px solid #e4e4e7', borderRadius: 6, padding: 12 }}>
      <Group justify="space-between" mb={4}>
        <Text fw={600} tt="capitalize">{job.vendor}</Text>
        <Badge size="sm" color={STATUS_COLOR[job.status]}>{STATUS_LABEL[job.status]}</Badge>
      </Group>

      {job.status === 'in_progress' && <Group gap="xs"><Loader size="xs" /><Text size="sm" c="dimmed">Looking up part…</Text></Group>}

      {job.status === 'needs_input' && job.question && (
        <Stack gap="xs">
          <Text size="sm">{job.question}</Text>
          <Group gap="xs">
            <TextInput value={answer} onChange={(e) => setAnswer(e.currentTarget.value)} placeholder="Your answer" style={{ flex: 1 }} />
            <Button size="xs" onClick={() => onAnswer(job.id, answer)} disabled={!answer.trim()}>Send</Button>
          </Group>
        </Stack>
      )}

      {job.status === 'done' && job.result && (
        <Stack gap={4}>
          <Group justify="space-between" align="center">
            <Stack gap={0}>
              <Text fw={500}>{job.result.name ?? '—'}</Text>
              <Text size="xs" c="dimmed">
                {[job.result.brand, job.result.sku].filter(Boolean).join(' · ')}
                {job.result.available != null && ` · ${job.result.available ? 'In stock' : 'Out of stock'}`}
              </Text>
            </Stack>
            <Stack gap={0} align="flex-end">
              <Text fw={700}>{job.result.price != null ? `$${Number(job.result.price).toFixed(2)}` : '—'}</Text>
              {job.result.source_url && (
                <Text size="xs" component="a" href={job.result.source_url} target="_blank" rel="noopener" c="blue">
                  view
                </Text>
              )}
            </Stack>
          </Group>
          <Button size="xs" variant="filled" mt={6} onClick={() => onApply(job.id)} disabled={job.result.price == null}>
            Use this price
          </Button>
        </Stack>
      )}

      {(job.status === 'failed' || job.status === 'expired') && (
        <Text size="sm" c="red">{job.error ?? 'Failed'}</Text>
      )}
    </Box>
  );
}
